/**
 * FmUnlockPreview — the shared previewable paid boundary (gap G5).
 *
 * ONE adapter, consumed by every archetype that has a paid boundary. It is built
 * on the existing `FmUnlockCard` primitive rather than being a second upsell
 * component, and it exists because a lock rendered as a grey sentence in a dead
 * band tells the owner nothing about what is withheld, what it costs, or how to
 * get it.
 *
 * The contract it enforces by shape:
 *
 * - `priceLabel` is SERVED. Nothing here computes or formats a price, and
 *   nothing renders an internal quota or credit count (OPJ-21).
 * - `previewNote` states what stays readable behind the card. A lock in this
 *   product is a preview, not a wall: full-fidelity content keeps rendering and
 *   the card sits with it.
 * - `unlockActionId` names an action the descriptor already declares, so the
 *   card can never be a dead end.
 *
 * FmFormFlowPage consumes it today. FmOperationalWorkspaceRoot adopts it in
 * 6wbfb.6.5, its first production adopter, which finalizes the adapter.
 */
import React from 'react';
import { FmUnlockCard } from './FmUnlockCard';

export interface FmUnlockPreviewDescriptor {
  /** What the owner gets, named as the package rather than as a feature flag. */
  title: string;
  /** What is withheld right now, in owner words. No counts, no internal quota. */
  summary: string;
  /** Served price copy. Never computed or formatted here. */
  priceLabel: string;
  /** What the owner can still read while the boundary holds. */
  previewNote: string;
  /** A declared action id that opens the purchase path. */
  unlockActionId: string;
  /** Owner-facing label for that action. */
  unlockLabel: string;
}

export interface FmUnlockPreviewProps {
  preview: FmUnlockPreviewDescriptor;
  onUnlock: () => void;
  'data-testid'?: string;
}

/**
 * Renders one previewable paid boundary: the package, its served price, what
 * stays readable without it, and the action that opens the purchase path.
 */
export const FmUnlockPreview: React.FC<FmUnlockPreviewProps> = ({
  preview,
  onUnlock,
  'data-testid': testId = 'unlock-preview',
}) => (
  <FmUnlockCard
    title={preview.title}
    unlocksSummary={preview.summary}
    priceLabel={preview.priceLabel}
    teaser={preview.previewNote}
    ctaLabel={preview.unlockLabel}
    onCtaClick={onUnlock}
    data-testid={testId}
    ctaTestId={`${testId}-cta`}
  />
);

FmUnlockPreview.displayName = 'FmUnlockPreview';

export default FmUnlockPreview;
